/**
 * Retry Utilities for Network Operations
 * ネットワーク不安定時の再送制御とエラーハンドリング
 */

/**
 * Exponential backoff delay calculation
 * @param {number} attempt - Current attempt number (0-based)
 * @param {number} baseDelay - Base delay in milliseconds (default: 1000)
 * @param {number} maxDelay - Maximum delay in milliseconds (default: 10000)
 * @returns {number} Delay in milliseconds
 */
export const calculateBackoffDelay = (attempt, baseDelay = 1000, maxDelay = 10000) => {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitteredDelay = exponentialDelay * (0.5 + Math.random() * 0.5); // Add jitter
  return Math.min(jitteredDelay, maxDelay);
};

/**
 * Retry function with exponential backoff
 * @param {Function} operation - Async operation to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxAttempts - Maximum retry attempts (default: 3)
 * @param {number} options.baseDelay - Base delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {Function} options.shouldRetry - Function to determine if error should trigger retry
 * @returns {Promise} Result of the operation
 */
export const retryWithBackoff = async (operation, options = {}) => {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    shouldRetry = (error) => true // Retry all errors by default
  } = options;

  let lastError;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      lastError = error;
      
      // Don't retry if shouldRetry function returns false
      if (!shouldRetry(error)) {
        throw error;
      }

      // Don't wait after the last attempt
      if (attempt < maxAttempts - 1) {
        const delay = calculateBackoffDelay(attempt, baseDelay, maxDelay);
        console.warn(`🔄 Retry attempt ${attempt + 1}/${maxAttempts} after ${delay}ms delay:`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  console.error(`❌ All retry attempts failed after ${maxAttempts} tries:`, lastError);
  throw lastError;
};

/**
 * Check if error is retryable (network errors, timeouts, 5xx status codes)
 * @param {Error} error - Error object to check
 * @returns {boolean} True if error should trigger retry
 */
export const isRetryableError = (error) => {
  // Network errors
  if (error.name === 'NetworkError' || error.message.includes('fetch')) {
    return true;
  }

  // Timeout errors
  if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
    return true;
  }

  // HTTP status codes that should be retried
  if (error.status) {
    // 5xx server errors
    if (error.status >= 500 && error.status < 600) {
      return true;
    }
    // 429 Too Many Requests
    if (error.status === 429) {
      return true;
    }
    // 408 Request Timeout
    if (error.status === 408) {
      return true;
    }
  }

  // Supabase specific errors that should be retried
  if (error.code) {
    // Temporary connection issues
    if (['PGRST301', 'PGRST302'].includes(error.code)) {
      return true;
    }
  }

  return false;
};

/**
 * Retry operation specifically for Supabase operations
 * @param {Function} supabaseOperation - Async Supabase operation
 * @param {string} operationName - Name for logging purposes
 * @returns {Promise} Result of the operation
 */
export const retrySupabaseOperation = async (supabaseOperation, operationName = 'Supabase operation') => {
  return retryWithBackoff(supabaseOperation, {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 8000,
    shouldRetry: isRetryableError
  });
};

/**
 * Queue for managing pending operations during network outages
 */
class OperationQueue {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
  }

  /**
   * Add operation to queue
   * @param {Function} operation - Async operation
   * @param {string} id - Unique operation ID
   * @param {Object} metadata - Additional metadata
   */
  enqueue(operation, id, metadata = {}) {
    this.queue.push({
      id,
      operation,
      metadata,
      timestamp: Date.now(),
      attempts: 0
    });

    // Start processing if not already running
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * Process queued operations
   */
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.queue.length > 0) {
      const item = this.queue.shift();
      
      try {
        await retryWithBackoff(item.operation, {
          maxAttempts: 3,
          shouldRetry: isRetryableError
        });
        console.log(`✅ Queued operation completed: ${item.id}`);
      } catch (error) {
        console.error(`❌ Queued operation failed: ${item.id}`, error);
        // Could implement dead letter queue or additional retry logic here
      }
    }

    this.isProcessing = false;
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      queueLength: this.queue.length,
      isProcessing: this.isProcessing
    };
  }
}

// Export singleton queue instance
export const operationQueue = new OperationQueue();